import { MainLayoutContainer } from "components/Layout";
import { useSurveyAnswer } from "./useSurveyAnswer";
import {
  Box,
  CardContent,
  Grid2 as Grid,
  Stack,
  Typography,
  Divider,
  Chip,
  TextField,
  Button,
} from "@mui/material";
import { CardOutline } from "components/Card";
import { SingleChoiceSelector } from "./components";
import { LikertScaleSelector } from "./components/LikertScaleSelector";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";

export const SurveyAnswer = () => {
  const {
    survey,
    loading,
    answers,
    updateAnswer,
    handleSubmit,
  } = useSurveyAnswer();

  if (loading || !survey) {
    return <div>loading...</div>;
  }

  const sortedQuestions = [...(survey.questions || [])].sort(
    (a, b) => (a.question?.order || 0) - (b.question?.order || 0)
  );

  return (
    <MainLayoutContainer
      title={survey.name}
      breadcrumb={BREADCRUMB_PAGE.BACK}
      loading={loading}
    >
      <Box mt={2}>
        <Grid container spacing={3}>
          {sortedQuestions.map((sq, index) => {
            const questionId = sq.question?.question_id || "";
            const questionType = sq.question?.type || "open_ended";
            const currentAnswer = answers[questionId];

            return (
              <Grid size={12} key={sq.survey_question_id || index}>
                <CardOutline sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Chip
                          label={`Question ${index + 1}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </Stack>

                      <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {sq.question?.title || "Untitled Question"}
                        </Typography>
                        {sq.question?.body && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {sq.question.body}
                          </Typography>
                        )}
                      </Box>

                      <Divider />

                      <Box>
                        {questionType === "open_ended" && (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            placeholder="Type your answer here..."
                            value={currentAnswer as string || ""}
                            onChange={(e) =>
                              updateAnswer(questionId, e.target.value)
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                              },
                            }}
                          />
                        )}

                        {questionType === "likert" && sq.question?.options && (
                          <LikertScaleSelector
                            value={currentAnswer !== undefined && currentAnswer !== null ? (currentAnswer as number) : null}
                            onChange={(value) => {
                              updateAnswer(questionId, value);
                            }}
                            options={sq.question.options}
                          />
                        )}
                        {(questionType === "single_choice" || questionType === "multiple_choice") && sq.question?.options && (
                          <SingleChoiceSelector
                            value={questionType === "single_choice" 
                              ? (currentAnswer !== undefined && currentAnswer !== null ? (currentAnswer as number) : null)
                              : undefined}
                            selectedValues={questionType === "multiple_choice"
                              ? (Array.isArray(currentAnswer) ? (currentAnswer as number[]) : [])
                              : []}
                            onChange={(value) => {
                              if (questionType === "single_choice") {
                                updateAnswer(questionId, value);
                              }
                            }}
                            onMultipleChange={(values) => {
                              if (questionType === "multiple_choice") {
                                updateAnswer(questionId, values);
                              }
                            }}
                            options={sq.question.options}
                            multiple={questionType === "multiple_choice"}
                          />
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </CardOutline>
              </Grid>
            );
          })}

          <Grid size={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                sx={{ px: 4, py: 1.5, borderRadius: 2 }}
              >
                Submit Survey
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MainLayoutContainer>
  );
};
